<!--
  /Users/xingyunx/workplace/ws/RingcentralAnalysisPortal/src/components/ui/mobile-help-button.vue
  Mobile-friendly help button component with tooltip/popover functionality
  Shows help text on hover (desktop) or click (mobile)
  RELEVANT FILES: CallAnalysisManifest.vue, Tooltip.vue, Popover.vue
-->
<script setup lang="ts">
import { ref } from 'vue';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useMobileDetection } from '@/composables/useMobileDetection';
import * as LucideIcons from 'lucide-vue-next';

interface Props {
  text: string;
}

const props = defineProps<Props>();

// State for popover (mobile)
const isPopoverOpen = ref(false);

// Use composable for reactive mobile detection
const { isDesktop } = useMobileDetection();

function getIconComponent(iconName: string) {
  return (LucideIcons as any)[iconName] || null;
}
</script>

<template>
  <!-- Desktop: Use Tooltip with hover -->
  <TooltipProvider v-if="isDesktop" :delay-duration="300" :skip-delay-duration="100">
    <Tooltip>
      <TooltipTrigger as-child>
        <button
          class="inline-flex items-center justify-center w-4 h-4 rounded-full bg-muted hover:bg-muted-foreground/20 transition-colors"
        >
          <component :is="getIconComponent('HelpCircle')" class="h-3 w-3 text-muted-foreground" />
        </button>
      </TooltipTrigger>
      <TooltipContent class="max-w-[350px]">
        <p>{{ props.text }}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>

  <!-- Mobile: Use Popover with click -->
  <Popover v-else v-model:open="isPopoverOpen">
    <PopoverTrigger as-child>
      <button
        class="inline-flex items-center justify-center w-4 h-4 rounded-full bg-muted active:bg-muted-foreground/20 transition-colors"
      >
        <component :is="getIconComponent('HelpCircle')" class="h-3 w-3 text-muted-foreground" />
      </button>
    </PopoverTrigger>
    <PopoverContent class="w-80 p-3" side="top" :side-offset="4">
      <p class="text-sm">{{ props.text }}</p>
    </PopoverContent>
  </Popover>
</template>
